import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import VodafoneBadge from './VodafoneBadge';

// Animazioni per il progresso AI VF (Vodafone).
const VF_CSS = `
@keyframes vfPing { 0%{transform:scale(.6);opacity:.7;} 100%{transform:scale(2.3);opacity:0;} }
@keyframes vfStripes { from{background-position:0 0;} to{background-position:44px 0;} }
.vfd-overlay { position:fixed; inset:0; background:rgba(15,23,42,.45); display:flex;
  align-items:center; justify-content:center; z-index:1000; }
.vfd-modal { width:430px; max-width:93vw; background:#fff; border-radius:16px; padding:22px;
  box-shadow:0 24px 70px rgba(0,0,0,.32); }
.vfd-logo { width:80px; height:80px; margin:2px auto 14px; border-radius:50%; position:relative;
  background:radial-gradient(circle at center,#cc0000 0%,#880000 70%); border:2px solid #e60000;
  display:flex; align-items:center; justify-content:center;
  box-shadow:0 0 28px rgba(230,0,0,.30); }
.vfd-logo .ring { position:absolute; inset:-8px; border-radius:50%; border:2px solid rgba(230,0,0,.30);
  animation:vfPing 2s ease-out infinite; }
.vfd-bar { height:11px; border-radius:7px; background:#e2e8f0; overflow:hidden; }
.vfd-bar-fill { height:100%; background-color:#e60000; transition:width .4s ease;
  background-image:linear-gradient(45deg, rgba(255,255,255,.28) 25%, transparent 25%, transparent 50%,
    rgba(255,255,255,.28) 50%, rgba(255,255,255,.28) 75%, transparent 75%, transparent);
  background-size:44px 44px; animation:vfStripes 1s linear infinite; }
.vfd-chip { position:fixed; right:18px; bottom:18px; z-index:1000; background:#990000; color:#fff;
  border-radius:30px; padding:9px 16px; display:flex; gap:10px; align-items:center; cursor:pointer;
  box-shadow:0 10px 28px rgba(0,0,0,.28); font-size:12px; }
.vfd-chip .pulse { width:9px; height:9px; border-radius:50%; background:#ff8080; position:relative; }
.vfd-chip .pulse::after { content:''; position:absolute; inset:0; border-radius:50%; border:2px solid #ff8080;
  animation:vfPing 1.6s ease-out infinite; }
.vfd-chip2 { font-size:11px; padding:2px 9px; border-radius:20px; font-weight:700;
  display:inline-flex; align-items:center; gap:4px; }
`;

// Finestra di progresso del job bulk Vodafone.
// Props: jobId (string), onDone (callback chiamata a completed/cancelled).
export default function VfProgress({ jobId, onDone }) {
  const [job, setJob] = useState(null);
  const [minimized, setMinimized] = useState(false);

  const poll = useCallback(() => {
    if (!jobId) return;
    axios.get(`${API}/gea/bulk/${jobId}`).then(r => {
      setJob(r.data);
      if (r.data.status === 'completed' || r.data.status === 'cancelled') {
        onDone?.();
      }
    }).catch(() => {});
  }, [jobId, onDone]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 2000);
    return () => clearInterval(t);
  }, [poll]);

  if (!job) return null;

  const pct = job.total ? Math.round((job.completed / job.total) * 100) : 0;

  const stop = async () => {
    try {
      await axios.post(`${API}/gea/bulk/${jobId}/stop`);
      toast.info('Interruzione richiesta');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore stop'); }
  };

  return (
    <>
      <style>{VF_CSS}</style>

      {minimized && (
        <div className="vfd-chip" onClick={() => setMinimized(false)} title="Riapri la finestra Vodafone">
          <span className="pulse" />
          <VodafoneBadge size={14} style={{ filter: 'brightness(10)' }} />
          AI VF {pct}% · {job.completed}/{job.total}
        </div>
      )}

      {!minimized && (
        <div className="vfd-overlay" onClick={() => setMinimized(true)}>
          <div className="vfd-modal" onClick={(e) => e.stopPropagation()}>

            <div className="vfd-logo">
              <span className="ring" />
              <VodafoneBadge size={38} style={{ filter: 'brightness(10)' }} />
            </div>

            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#880000' }}>
                AI Vodafone in corso
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
              <span><b>{job.completed}</b>/{job.total} ({pct}%)</span>
            </div>
            <div className="vfd-bar">
              <div className="vfd-bar-fill" style={{ width: `${pct}%` }} />
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', margin: '14px 0' }}>
              <span className="vfd-chip2" style={{ background: 'rgba(230,0,0,.12)', color: '#cc0000' }}>
                <VodafoneBadge size={11} /> {job.vodafone ?? 0}{job.nuovi > 0 ? ` (${job.nuovi} nuovi)` : ''}
              </span>
              <span className="vfd-chip2" style={{ background: 'rgba(148,163,184,.18)', color: '#475569' }}>
                ✗ {job.negativi ?? 0}
              </span>
              <span className="vfd-chip2" style={{ background: 'rgba(99,102,241,.12)', color: '#4338ca' }}>
                ↷ {job.saltati ?? 0}
              </span>
              <span className="vfd-chip2" style={{ background: 'rgba(245,158,11,.14)', color: '#b45309' }}>
                ⚡ Disservizi {job.disservizi ?? 0}
              </span>
              <span className="vfd-chip2" style={{ background: 'rgba(239,68,68,.14)', color: '#b91c1c' }}>
                ⚠ {job.errori ?? 0}
              </span>
            </div>

            {job.current && (
              <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginBottom: 14 }}>
                In corso: <code>{job.current}</code>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setMinimized(true)}>
                Metti in background
              </button>
              <button
                className="btn"
                style={{ flex: 1, background: '#ef4444', color: '#fff', border: 'none' }}
                onClick={stop}
              >
                Stop
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
